/**
 * CoinGecko-powered local asset utilities
 * 
 * Enhanced symbol asset management with:
 * - Local asset prioritization 
 * - CoinGecko CDN fallbacks
 * - Progressive loading (WebP → PNG → SVG → External)
 * - Intelligent caching with market cap awareness
 * - Asset preloading for top-ranked symbols
 */

// Simple LRU Cache implementation
class SimpleLRUCache<T> {
  private cache = new Map<string, { value: T; timestamp: number }>();
  private maxSize: number;
  private ttl: number;

  constructor(maxSize: number, ttl: number) {
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(key: string): T | undefined {
    const item = this.cache.get(key);
    if (!item) return undefined;

    const now = Date.now();
    if (now - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return undefined;
    }

    // Move to end (LRU)
    this.cache.delete(key);
    this.cache.set(key, item);
    return item.value;
  }

  set(key: string, value: T): void {
    const now = Date.now();
    
    // Remove oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, { value, timestamp: now });
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }

  get max(): number {
    return this.maxSize;
  }
}

// Types
export interface AssetMetadata {
  symbol: string;
  coingeckoId?: string;
  marketCapRank?: number;
  localAvailable: boolean;
  lastVerified?: string;
}

export interface AssetRequest {
  symbol: string;
  size?: 'thumb' | 'small' | 'large';
  format?: 'webp' | 'png' | 'svg';
  fallback?: boolean;
}

export interface AssetResponse {
  url: string;
  source: 'local-webp' | 'local-png' | 'local-svg' | 'coingecko' | 'svg-fallback' | 'external-fallback';
  cached: boolean;
  metadata?: AssetMetadata;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const ASSET_CONFIG = {
  baseUrl: `${API_BASE_URL}/symbol-assets`,
  localStaticUrl: `${API_BASE_URL}/assets/crypto-icons`,
  cacheSize: 500,
  cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
  preloadTopCount: 100,
  coinGeckoBaseUrl: 'https://coin-images.coingecko.com/coins/images'
};

// Debug logging for configuration
console.log(`[CoinGecko Assets] API Base URL: ${API_BASE_URL}`);
console.log(`[CoinGecko Assets] Symbol Assets URL: ${ASSET_CONFIG.baseUrl}`);
console.log(`[CoinGecko Assets] Local Assets URL: ${ASSET_CONFIG.localStaticUrl}`);

// In-memory cache for asset URLs and metadata
const assetCache = new SimpleLRUCache<AssetResponse>(
  ASSET_CONFIG.cacheSize,
  ASSET_CONFIG.cacheTTL
);

const metadataCache = new SimpleLRUCache<AssetMetadata>(
  ASSET_CONFIG.cacheSize,
  ASSET_CONFIG.cacheTTL
);

// Market cap rankings cache (refreshed more frequently)
let marketCapRankings: string[] = [];
let rankingsLastFetched = 0;
const RANKINGS_CACHE_TTL = 60 * 60 * 1000; // 1 hour

/**
 * Clean and normalize symbol for consistent processing
 */
export function normalizeSymbol(symbol: string): string {
  if (!symbol) return '';
  
  // Remove common suffixes and normalize (fixed regex to properly anchor alternatives to end)
  return symbol
    .toUpperCase()
    .replace(/(USDT|BUSD|BTC|ETH)$/, '')
    .trim();
}

/**
 * Generate cache key for asset requests
 */
function getCacheKey(symbol: string, size: string, format: string): string {
  return `${normalizeSymbol(symbol)}_${size}_${format}`;
}

/**
 * Fetch asset metadata from backend API
 */
async function fetchAssetMetadata(symbol: string): Promise<AssetMetadata | null> {
  try {
    const response = await fetch(`${ASSET_CONFIG.baseUrl}/symbols/${symbol}/metadata`);
    
    if (response.ok) {
      const metadata = await response.json();
      return {
        symbol: metadata.symbol,
        coingeckoId: metadata.coingecko_id,
        marketCapRank: metadata.market_cap_rank,
        localAvailable: metadata.local_available,
        lastVerified: metadata.last_verified
      };
    }
  } catch (error) {
    console.warn(`Failed to fetch metadata for ${symbol}:`, error);
  }
  
  return null;
}

/**
 * Check if a URL is accessible (for validation)
 * Optimized to skip HEAD requests for endpoints that don't support them
 */
async function isUrlAccessible(url: string): Promise<boolean> {
  try {
    // For symbol-assets endpoints, skip HEAD and use GET directly (they don't support HEAD)
    if (url.includes('/symbol-assets/')) {
      const getResponse = await fetch(url, { method: 'GET' });
      return getResponse.ok;
    }
    
    // For other endpoints, try HEAD first (more efficient)
    const headResponse = await fetch(url, { method: 'HEAD' });
    
    // If HEAD succeeds, URL is accessible
    if (headResponse.ok) {
      return true;
    }
    
    // If HEAD fails with 405 (Method Not Allowed), try GET as fallback
    if (headResponse.status === 405) {
      console.log(`[isUrlAccessible] HEAD not supported for ${url}, trying GET...`);
      const getResponse = await fetch(url, { method: 'GET' });
      return getResponse.ok;
    }
    
    // For other HTTP errors, URL is not accessible
    return false;
  } catch (error) {
    console.log(`[isUrlAccessible] Error checking ${url}:`, error);
    return false;
  }
}

/**
 * Generate CoinGecko CDN URL for a symbol
 */
async function getCoinGeckoUrl(symbol: string, size: string): Promise<string | null> {
  try {
    // First try to get CoinGecko ID from our backend
    const metadata = await fetchAssetMetadata(symbol);
    
    if (metadata?.coingeckoId) {
      // Map size to CoinGecko size
      const sizeMap = {
        'thumb': 'thumb',
        'small': 'small', 
        'large': 'large'
      };
      
      const cgSize = sizeMap[size as keyof typeof sizeMap] || 'small';
      return `${ASSET_CONFIG.coinGeckoBaseUrl}/${metadata.coingeckoId}/${cgSize}/${metadata.coingeckoId}.png`;
    }
    
    // If no CoinGecko ID, try the direct API
    const response = await fetch(`${ASSET_CONFIG.baseUrl}/symbols/${symbol}/coingecko-url?size=${size}`);
    
    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
  } catch (error) {
    console.warn(`Failed to get CoinGecko URL for ${symbol}:`, error);
  }
  
  return null;
}

/**
 * Generate SVG fallback URL
 */
function getSvgFallbackUrl(symbol: string, size: string): string {
  return `${ASSET_CONFIG.baseUrl}/symbols/${symbol}/logo?size=${size}&fallback=svg`;
}

/**
 * Generate external fallback URL (legacy)
 */
function getExternalFallbackUrl(symbol: string): string {
  return `https://static-app.bb-os.com/icon/${normalizeSymbol(symbol)}.png`;
}

/**
 * Get local asset URL with format preference
 */
function getLocalAssetUrl(symbol: string, size: string, format: string): string {
  const normalizedSymbol = normalizeSymbol(symbol);
  
  if (format === 'api') {
    // Use our backend API endpoint
    return `${ASSET_CONFIG.baseUrl}/symbols/${symbol}/logo?size=${size}`;
  }
  
  // Direct static file access
  const sizeFolder = size === 'thumb' ? 'small' : size; // thumb uses small folder
  return `${ASSET_CONFIG.localStaticUrl}/${format}/${sizeFolder}/${normalizedSymbol}.${format}`;
}

/**
 * Fetch market cap rankings for prioritization
 */
async function fetchMarketCapRankings(): Promise<void> {
  const now = Date.now();
  
  if (now - rankingsLastFetched < RANKINGS_CACHE_TTL && marketCapRankings.length > 0) {
    return; // Use cached rankings
  }
  
  try {
    const response = await fetch(`${ASSET_CONFIG.baseUrl}/market-rankings?limit=500`);
    
    if (response.ok) {
      const data = await response.json();
      marketCapRankings = data.rankings || [];
      rankingsLastFetched = now;
    }
  } catch (error) {
    console.warn('Failed to fetch market cap rankings:', error);
  }
}

/**
 * Get priority score for a symbol (lower = higher priority)
 */
function getSymbolPriority(symbol: string): number {
  const normalizedSymbol = normalizeSymbol(symbol);
  const index = marketCapRankings.indexOf(normalizedSymbol);
  return index >= 0 ? index : 999999; // High number for unknown symbols
}

/**
 * Enhanced symbol asset URL generation with progressive fallback strategy
 */
export async function getSymbolAssetUrl(request: AssetRequest): Promise<AssetResponse> {
  const {
    symbol,
    size = 'small',
    format = 'png',
    fallback = true
  } = request;
  
  const normalizedSymbol = normalizeSymbol(symbol);
  const cacheKey = getCacheKey(normalizedSymbol, size, format);
  
  // Check cache first
  const cached = assetCache.get(cacheKey);
  if (cached) {
    return { ...cached, cached: true };
  }
  
  // Fetch metadata for context
  let metadata = metadataCache.get(normalizedSymbol);
  if (!metadata) {
    const fetchedMetadata = await fetchAssetMetadata(normalizedSymbol);
    if (fetchedMetadata) {
      metadata = fetchedMetadata;
      metadataCache.set(normalizedSymbol, metadata);
    }
  }
  
  // Progressive fallback strategy
  const attempts = [
    // 1. Local WebP (modern browsers, best compression)
    ...(format === 'webp' || format === 'png' ? [{
      url: getLocalAssetUrl(normalizedSymbol, size, 'webp'),
      source: 'local-webp' as const
    }] : []),
    
    // 2. Local PNG (universal compatibility)
    ...(format === 'png' || format === 'webp' ? [{
      url: getLocalAssetUrl(normalizedSymbol, size, 'png'),
      source: 'local-png' as const
    }] : []),
    
    // 3. Backend API endpoint (handles all logic server-side)
    {
      url: getLocalAssetUrl(normalizedSymbol, size, 'api'),
      source: 'local-svg' as const
    }
  ];
  
  // Try local assets first
  for (const attempt of attempts) {
    try {
      const isAccessible = await isUrlAccessible(attempt.url);
      if (isAccessible) {
        const response: AssetResponse = {
          url: attempt.url,
          source: attempt.source,
          cached: false,
          metadata
        };
        
        assetCache.set(cacheKey, response);
        return response;
      }
    } catch {
      continue; // Try next option
    }
  }
  
  // If local assets failed and fallback is enabled
  if (fallback) {
    // 4. Try CoinGecko CDN
    try {
      const coinGeckoUrl = await getCoinGeckoUrl(normalizedSymbol, size);
      if (coinGeckoUrl && await isUrlAccessible(coinGeckoUrl)) {
        const response: AssetResponse = {
          url: coinGeckoUrl,
          source: 'coingecko',
          cached: false,
          metadata
        };
        
        assetCache.set(cacheKey, response);
        return response;
      }
    } catch {
      // Continue to next fallback
    }
    
    // 5. SVG fallback (always works)
    const svgUrl = getSvgFallbackUrl(normalizedSymbol, size);
    const response: AssetResponse = {
      url: svgUrl,
      source: 'svg-fallback',
      cached: false,
      metadata
    };
    
    assetCache.set(cacheKey, response);
    return response;
  }
  
  // Final fallback: external URL (legacy)
  const externalUrl = getExternalFallbackUrl(normalizedSymbol);
  const response: AssetResponse = {
    url: externalUrl,
    source: 'external-fallback',
    cached: false,
    metadata
  };
  
  assetCache.set(cacheKey, response);
  return response;
}

/**
 * Batch asset URL generation for multiple symbols
 */
export async function getSymbolAssetUrls(requests: AssetRequest[]): Promise<AssetResponse[]> {
  // Process high-priority symbols first
  await fetchMarketCapRankings();
  
  const sortedRequests = requests.sort((a, b) => 
    getSymbolPriority(a.symbol) - getSymbolPriority(b.symbol)
  );
  
  // Process in parallel but with some rate limiting
  const batchSize = 10;
  const results: AssetResponse[] = [];
  
  for (let i = 0; i < sortedRequests.length; i += batchSize) {
    const batch = sortedRequests.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(request => getSymbolAssetUrl(request))
    );
    results.push(...batchResults);
  }
  
  // Restore original order
  const resultMap = new Map();
  results.forEach((result, index) => {
    resultMap.set(sortedRequests[index].symbol, result);
  });
  
  return requests.map(request => resultMap.get(request.symbol));
}

/**
 * Preload assets for top market cap symbols
 */
export async function preloadTopSymbolAssets(): Promise<void> {
  try {
    await fetchMarketCapRankings();
    
    const topSymbols = marketCapRankings
      .slice(0, ASSET_CONFIG.preloadTopCount)
      .map(symbol => ({ symbol, size: 'small' as const }));
    
    // Preload in background without waiting
    getSymbolAssetUrls(topSymbols).catch(error => {
      console.warn('Failed to preload top symbol assets:', error);
    });
    
  } catch (error) {
    console.warn('Failed to preload symbol assets:', error);
  }
}

/**
 * Clear asset cache (useful for testing or manual refresh)
 */
export function clearAssetCache(): void {
  assetCache.clear();
  metadataCache.clear();
  marketCapRankings = [];
  rankingsLastFetched = 0;
}

/**
 * Get cache statistics for monitoring
 */
export function getAssetCacheStats() {
  return {
    assetCache: {
      size: assetCache.size,
      maxSize: assetCache.max,
      utilization: assetCache.size / assetCache.max
    },
    metadataCache: {
      size: metadataCache.size,
      maxSize: metadataCache.max
    },
    marketCapRankings: {
      count: marketCapRankings.length,
      lastFetched: new Date(rankingsLastFetched).toISOString()
    }
  };
}

// Export default configuration for testing/debugging
export { ASSET_CONFIG };